import { Dictionary } from 'vuex';

import { AuthResource, authResource } from '@/resources/auth.resource';
import { AbstractModule } from '../abstract.module';

class CheckPermissionModule extends AbstractModule<AuthResource> {
  /**
   * Timeout kolejki pobierania
   *
   * @private
   * @type {*}
   * @memberof AuthModule
   */
  private permissionQueueTimeout: any;

  /**
   * Lista uprawnień do pobrania
   *
   * @private
   * @type {string[]}
   * @memberof AuthModule
   */
  private toCheck: string[] = [];

  /**
   * Lista resolverów promisów do wywołania
   *
   * @private
   * @memberof AuthModule
   */
  private resolvers: Array<() => any> = [];

  public namespaced = false;

  public state = { permissions: {} };

  public mutations = {
    /**
     * Zapisuje uprawnienia
     *
     * @param {*} localState
     * @param {{permissions: string[], result: boolean[]}} data
     */
    savePermissions: (localState: any, data: { permissions: string[]; result: boolean[] }): void => {
      const permissionsObj = data.permissions.reduce((obj: any, permission: string, index: number) => {
        obj[permission] = data.result[index];
        return obj;
      }, {});
      localState.permissions = { ...localState.permissions, ...permissionsObj };
      this.resolvers.forEach(resolver => resolver());
      this.resolvers = [];
    },

    /**
     * Czyści zapisane uprawnienia
     *
     * @param {*} localState
     */
    clearPermissions: (localState: any): void => {
      localState.permissions = {};
      this.toCheck = [];
    }
  };
  public actions = {
    /**
     * Sprawdzenie uprawnień synchroniczne
     *
     * @param context context
     */
    checkPermissionsSync: async (context: any, permissions: string | string[]): Promise<Dictionary<boolean>> => {
      if (!Array.isArray(permissions)) {
        permissions = [permissions];
      }

      const toCheck = permissions.filter(permission => context.state.permissions[permission] === undefined);

      return this.resource
        .checkPermissions(toCheck)
        .then(result => context.commit('savePermissions', { permissions: toCheck, result }))
        .then(() =>
          (permissions as string[]).reduce(
            (obj, permission) => ({ ...obj, [permission]: context.state.permissions[permission] }),
            {}
          )
        );
    },

    /**
     * Sprawdzenie uprawnień
     *
     * @param context context
     */
    checkPermissions: async (context: any, permissions: string | string[]): Promise<Dictionary<boolean>> => {
      if (!Array.isArray(permissions)) {
        permissions = [permissions];
      }

      const toCheck = permissions.filter(permission => context.state.permissions[permission] === undefined);

      if (toCheck.length) {
        await this.queuePermissionsCheck(context, toCheck);
      }

      return permissions.reduce(
        (obj, permission) => ({ ...obj, [permission]: context.state.permissions[permission] }),
        {}
      );
    }
  };

  private queuePermissionsCheck(context: any, permissions: string[]): Promise<void> {
    if (this.permissionQueueTimeout) {
      clearTimeout(this.permissionQueueTimeout);
    }

    this.toCheck.push(...permissions.filter(permission => !this.toCheck.includes(permission)));

    return new Promise(resolve => {
      this.resolvers.push(resolve);
      this.permissionQueueTimeout = setTimeout(() => {
        const toCheck = this.toCheck.filter(permission => context.state.permissions[permission] === undefined);
        this.resource
          .checkPermissions(toCheck)
          .then(result => context.commit('savePermissions', { permissions: toCheck, result }));
      }, 0);
    });
  }
}

export default new CheckPermissionModule(authResource);
